import { useState, useEffect } from "react";
import axios from "axios";
import { MapPin, Clock, Phone, Heart, ArrowRight } from "lucide-react";
import { Footer } from "../components/layout/Footer";
import { useLanguage } from "../context/LanguageContext";
import { API } from "../lib/api";

export default function RestaurantsPage() {
  const [restaurants, setRestaurants] = useState([]);
  const [townFilter, setTownFilter] = useState("all");
  const [priceFilter, setPriceFilter] = useState("all");
  const [romanticFilter, setRomanticFilter] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    axios.get(`${API}/restaurants`).then(res => setRestaurants(res.data)).catch(console.error);
  }, []);

  const towns = ["all", ...new Set(restaurants.map(r => r.town))];

  const filteredRestaurants = restaurants.filter(r => {
    if (townFilter !== "all" && r.town !== townFilter) return false;
    if (priceFilter !== "all" && r.price_range !== priceFilter) return false;
    if (romanticFilter && !r.romantic) return false;
    return true;
  });

  // Generate descriptive alt text for restaurant images
  const getRestaurantAltText = (restaurant) => {
    const cuisine = restaurant.cuisine_type || 'Italian';
    const features = [];
    if (restaurant.romantic) features.push('romantic setting');
    if (restaurant.outdoor_seating) features.push('outdoor terrace');
    const featureText = features.length > 0 ? ` with ${features.join(' and ')}` : '';
    return `${restaurant.name}: ${cuisine} restaurant in ${restaurant.town}, Puglia${featureText}`;
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="restaurants-page">
      <header className="bg-teal-700 text-white px-6 pt-8 pb-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-serif">{t('whereToEat')}</h1>
          <p className="text-teal-200 text-sm mt-2">{t('verifiedRestaurants')}</p>
        </div>
      </header>

      <div className="px-6 py-4 max-w-4xl mx-auto">
        <div className="flex gap-2 flex-wrap mb-3">
          {[
            { id: "all", label: t('allPrices') },
            { id: "€", label: `€ ${t('budget')}` },
            { id: "€€", label: `€€ ${t('mid')}` },
            { id: "€€€", label: `€€€ ${t('fine')}` },
            { id: "€€€€", label: `€€€€ ${t('luxury')}` }
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setPriceFilter(f.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                priceFilter === f.id ? "bg-teal-700 text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-teal-400"
              }`}
              aria-pressed={priceFilter === f.id}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-3 items-center">
          <select 
            value={townFilter}
            onChange={(e) => setTownFilter(e.target.value)}
            className="px-3 py-2 rounded-xl border border-stone-200 text-sm text-stone-700 bg-white"
            aria-label={t('allTowns')}
          >
            <option value="all">{t('allTowns')}</option>
            {towns.filter(t => t !== "all").map(town => (
              <option key={town} value={town}>{town}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-stone-600 cursor-pointer">
            <input 
              type="checkbox" 
              checked={romanticFilter} 
              onChange={(e) => setRomanticFilter(e.target.checked)}
              className="rounded border-stone-300 text-teal-600 focus:ring-teal-500"
              aria-describedby="romantic-filter-desc"
            />
            <span id="romantic-filter-desc">{t('romantic')}</span>
          </label>
        </div>
      </div>

      <div className="px-6 py-4 max-w-4xl mx-auto space-y-4 pb-24 md:pb-8">
        {filteredRestaurants.map((restaurant) => (
          <article 
            key={restaurant.id} 
            className="bg-white rounded-2xl p-5 border border-stone-100 shadow-sm" 
            data-testid={`restaurant-card-${restaurant.id}`}
            aria-labelledby={`restaurant-name-${restaurant.id}`}
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <h2 id={`restaurant-name-${restaurant.id}`} className="font-serif text-xl text-stone-800">
                  {restaurant.name}
                </h2>
                <p className="text-sm text-stone-500 flex items-center gap-1.5 mt-1">
                  <MapPin size={14} aria-hidden="true" /> {restaurant.town} · {restaurant.cuisine_type}
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-teal-700" aria-label={`Price range: ${restaurant.price_range}`}>
                  {restaurant.price_range}
                </p>
                {restaurant.avg_price && (
                  <p className="text-xs text-stone-500">~€{restaurant.avg_price}/{t('perPerson')}</p>
                )}
              </div>
            </div>
            
            <p className="text-[15px] text-stone-600">{restaurant.description}</p>
            
            {restaurant.specialties && restaurant.specialties.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" role="list" aria-label="Specialties">
                {restaurant.specialties.slice(0, 3).map((s, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700" role="listitem">
                    {s}
                  </span>
                ))}
              </div>
            )}
            
            <div className="flex flex-wrap gap-2 mt-3" role="list" aria-label="Features">
              {restaurant.romantic && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-pink-50 text-pink-700 flex items-center gap-1" role="listitem">
                  <Heart size={12} aria-hidden="true" /> {t('romantic')}
                </span>
              )}
              {restaurant.family_friendly && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700" role="listitem">
                  {t('familyFriendly')}
                </span>
              )}
              {restaurant.outdoor_seating && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-green-50 text-green-700" role="listitem">
                  {t('outdoorSeating')}
                </span>
              )}
            </div>
            
            {restaurant.hours && (
              <p className="text-sm text-stone-500 mt-3 flex items-center gap-1.5">
                <Clock size={14} aria-hidden="true" /> {restaurant.hours}
              </p>
            )}
            
            <div className="mt-4 pt-4 border-t border-stone-100 flex flex-wrap gap-3">
              {restaurant.booking_url ? (
                <a 
                  href={restaurant.booking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-teal-700 hover:bg-teal-800 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
                  aria-label={`${t('bookTable')} at ${restaurant.name}`}
                >
                  {t('bookTable')} <ArrowRight size={14} aria-hidden="true" />
                </a>
              ) : restaurant.phone && (
                <a 
                  href={`tel:${restaurant.phone}`}
                  className="bg-teal-700 hover:bg-teal-800 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
                  aria-label={`${t('callToReserve')} ${restaurant.name}`}
                >
                  <Phone size={14} aria-hidden="true" /> {t('callToReserve')}
                </a>
              )}
            </div>
          </article>
        ))}
      </div>
      <Footer />
    </div>
  );
}
